import { conceptGroupDomainMap } from '@verantos-dev/app-state-cohort-builder';

import type { ConceptGroupOption, ConceptGroupsByDomain } from './conceptGroupOption';

/**
 * Returns the set of concept groups that match the domain filter for a given
 * concept attribute by consulting the centralized `conceptGroupDomainMap` and
 * the pre-computed `conceptGroupsByDomain` index.
 *
 * Falls back to the full `availableConceptGroups` list when no attribute or
 * domain config is available (open filter).
 */
export function getConceptGroupsForAttribute(
  conceptAttribute: string | undefined,
  conceptGroupsByDomain: ConceptGroupsByDomain | undefined,
  availableConceptGroups: ConceptGroupOption[]
): ConceptGroupOption[] {
  if (!conceptAttribute || !conceptGroupsByDomain) return availableConceptGroups;

  const domainConfig = conceptGroupDomainMap?.[conceptAttribute]?.default;
  if (!domainConfig) {
    return availableConceptGroups;
  }

  const { domainsToInclude, domainsToExclude } = domainConfig;

  // No domain constraints → return full list
  if (!domainsToInclude.length && !domainsToExclude.length) return availableConceptGroups;

  if (domainsToInclude.length > 0) {
    // Collect concept groups from all included domains (union, deduplicated)
    const seen = new Set<string>();
    const result: ConceptGroupOption[] = [];
    for (const domain of domainsToInclude) {
      const groups = conceptGroupsByDomain.get(domain.toLowerCase());
      if (!groups) continue;
      for (const cg of groups) {
        if (!seen.has(cg.conceptGroupId)) {
          seen.add(cg.conceptGroupId);
          result.push(cg);
        }
      }
    }
    return result;
  }

  // Only exclusions — exclude only if ALL of a concept group's domains are excluded
  const excludedSet = new Set(domainsToExclude.map((d) => d.toLowerCase()));
  return availableConceptGroups.filter((cg) => {
    if (!cg.domains) return true; // no domain info → keep
    const cgDomains = cg.domains.split(/,\s*/).map((d) => d.toLowerCase());
    // Keep if ANY domain is allowed (not in exclusion set)
    return cgDomains.some((d) => !excludedSet.has(d));
  });
}
